import * as React from 'react';
import { Map as MapRef } from '@cian/frontend-newbuilding-map-component';
import { useDispatch, useSelector } from 'react-redux';
import { selectFilters, selectIsSuburban } from '../../../selectors/filters';
import {
  ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES,
  IInfrastructure,
  IInfrastructureType,
  IInfrastructureResponse,
  onInfrastructureObjectsFetch,
  IInfrastructureProps,
  IMetroStation,
  trackZoomControlClick,
  IRoad,
  ICoordinates,
  DEFAULT_MAP_CENTER,
} from '../utils';
import { toInfrastructureTypes, getInfrastructureMapByYear, toGeoObjects } from '../helpers';
import { selectGeocodeCoordinates } from '../../../selectors/geocode';
import { IControl } from '@cian/frontend-newbuilding-map-component/es/types/control';
import { useIsDesktop, useIsFirstRun } from '@cian/valuation-utils-component';
import { getGeocode } from '../../../actions/geocode';
import { getEstimationFiltersHash } from '../../../selectors/estimation';
import { TThunkDispatch } from '../../../types/redux';

const CURRENT_YEAR = new Date().getFullYear();

export const useInfrastructureProps = (): IInfrastructureProps | null => {
  const isDesktop = useIsDesktop();
  const isFirstRun = useIsFirstRun();
  const dispatch = useDispatch<TThunkDispatch>();

  const { address } = useSelector(selectFilters);
  const hashEstimation = useSelector(getEstimationFiltersHash);
  const coordinates = useSelector(selectGeocodeCoordinates);
  const isSuburban = useSelector(selectIsSuburban);

  const [centeredCoordinates, setCenteredCoordinates] = React.useState<ICoordinates | null>(null);

  const mapRef = React.useRef<MapRef>(null);
  const infrastructureTypeRef = React.useRef<string | null>(null);
  const infrastructureByYearRef = React.useRef(new Map<number, IInfrastructure>());

  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const [currentTypes, setCurrentTypes] = React.useState(ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES);

  const [infrastructureTypes, setInfrastructureTypes] = React.useState<IInfrastructureType[] | null>(null);
  const [metroStations, setMetroStations] = React.useState<IMetroStation[]>([]);
  const [roads, setRoads] = React.useState<IRoad[]>([]);

  const suggestPosition = isDesktop ? 'bottom' : 'top';

  let controls: IControl[] = [{ type: 'ZoomControl', onClick: trackZoomControlClick }];

  if (isDesktop) {
    controls = [
      ...controls,
      { type: 'GeolocationControl' },
      { type: 'PanoramaControl' },
      { type: 'RulerControl' },
      { type: 'FullscreenControl' },
    ];
  }

  const setInfrastructureType = React.useCallback((types: string[]) => {
    infrastructureTypeRef.current = types.find(type => !ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES.includes(type)) ?? null;
    setCurrentTypes(types);
  }, []);

  const handleMapLoaded = React.useCallback(() => setIsMapLoaded(true), []);

  const handleInfrastructureFetch = React.useCallback(async () => {
    const response = await onInfrastructureObjectsFetch({ address });

    const existingInfrastructure = response.infrastructure;
    const metro = response.metro;

    setMetroStations(metro as IMetroStation[]);

    if (isSuburban) {
      setRoads(response.roads);
    }

    if (existingInfrastructure) {
      const { infrastructure } = existingInfrastructure;

      const types = toInfrastructureTypes(infrastructure as unknown as IInfrastructureResponse[]);
      setInfrastructureTypes(types);

      const infrastructureMapByYear = getInfrastructureMapByYear({
        infrastructureItems: infrastructure as unknown as IInfrastructureResponse[],
        centeredCoordinates: centeredCoordinates ?? DEFAULT_MAP_CENTER,
        year: CURRENT_YEAR,
      });
      infrastructureByYearRef.current = infrastructureMapByYear;

      const geoObjects = toGeoObjects({
        infrastructureByYear: infrastructureByYearRef.current,
        year: CURRENT_YEAR,
      });

      return Promise.resolve(geoObjects);
    } else {
      return Promise.resolve([]);
    }
  }, [address, centeredCoordinates]);

  React.useEffect(() => {
    if (coordinates) {
      setCenteredCoordinates({
        lng: coordinates[0],
        lat: coordinates[1],
      });
    }
  }, [coordinates]);

  React.useEffect(() => {
    if (hashEstimation && !isFirstRun) {
      setCenteredCoordinates(null);
      dispatch(getGeocode());
    }
  }, [hashEstimation]);

  if (!centeredCoordinates) {
    return null;
  }

  return {
    mapRef,
    isMapLoaded,
    centeredCoordinates,
    currentTypes,
    metroStations,
    roads,
    infrastructureByYearRef,
    infrastructureTypes,
    setInfrastructureType,
    handleMapLoaded,
    controls,
    suggestPosition,
    onGeoObjectsFetch: handleInfrastructureFetch,
  };
};
